import type { ReportListItem } from './reportsClient'

/**
 * Output of `planPqrDefaults`. Drives the seed values of the PQR No. /
 * Rev. inputs on the pre-generate picker, plus the one-line "last
 * generated" hint rendered under each weld row.
 *
 * `lastGeneratedHint` is `null` when the weld has no surviving prior
 * PQRs (the picker hides the hint line in that case). When a prior
 * exists but the row pre-dates the provenance change,
 * `generatedByEmail` is `null` and the UI omits the trailing
 * "by <email>" segment.
 */
export interface PqrPlannerResult {
  defaultPqrNumber: string
  defaultRevision: string
  lastGeneratedHint: {
    pqrNumber: string
    revision: string
    uploadedAt: string
    generatedByEmail: string | null
  } | null
}

export interface PqrPlannerInput {
  reports: ReportListItem[]
  /**
   * Single-weld input (legacy / convenience). When supplied without
   * `weldIds`, the planner treats it as `weldIds: [weldId]`. Either
   * `weldId` or `weldIds` MUST be provided.
   */
  weldId?: string
  /**
   * Multi-weld input (added in `multi-weld-pqr`). The "train" identity
   * is `[...weldIds].sort().join('|')` — reports match this train when
   * their stored weld-set (`r.weldIds ?? [r.weldId]`, normalized the
   * same way) equals it. Order in the array doesn't matter; the
   * planner sorts internally.
   */
  weldIds?: string[]
}

/**
 * Normalize a weld-set to its train-identity key. Returns `''` for
 * empty / undefined inputs so callers can short-circuit.
 */
function trainKey(weldIds: string[] | null | undefined): string {
  if (!weldIds || weldIds.length === 0) return ''
  return [...weldIds].map((s) => s.trim()).filter(Boolean).sort().join('|')
}

/**
 * Pull the report's stored weld-set, preferring the new `weldIds[]`
 * field and falling back to the legacy single `weldId` (treated as a
 * 1-element set). Returns `[]` when neither is populated.
 */
function reportWeldSet(r: ReportListItem): string[] {
  if (Array.isArray(r.weldIds) && r.weldIds.length > 0) {
    return r.weldIds.filter((s): s is string => typeof s === 'string' && s.length > 0)
  }
  if (typeof r.weldId === 'string' && r.weldId.length > 0) return [r.weldId]
  return []
}

/**
 * Treat anything that does not parse cleanly as a base-10 integer as `0`
 * (per AC: "Non-numeric stored revs are treated as `0` for bump
 * arithmetic"). Negative numbers and floats also collapse to `0` to
 * avoid surprising bumps when the field was hand-edited.
 */
function revAsNumber(rev: string | undefined): number {
  if (typeof rev !== 'string') return 0
  const trimmed = rev.trim()
  if (!/^\d+$/.test(trimmed)) return 0
  return parseInt(trimmed, 10)
}

function isLive(report: ReportListItem): boolean {
  return !(typeof report.deletedAt === 'string' && report.deletedAt.trim().length > 0)
}

/**
 * Compute the picker's default PQR No. / Rev. and the optional
 * "last generated" hint for a given weld.
 *
 * Branches (mirrors the issue's AC):
 *
 * 1. Weld has **prior PQRs** (surviving = not soft-deleted) → reuse
 *    the most-recently-generated PQR No. for that weld, and propose
 *    `max(rev for (weld, that PQR No.)) + 1`. The hint shows the
 *    single most-recent surviving row, with the email from
 *    `generatedBy.email` when present.
 *
 * 2. Weld has **no priors** → propose the next free PQR No. across
 *    the whole pipe set: `PQR-<NNN>` where NNN = (unique surviving
 *    PQR Nos seen) + 1, zero-padded to three digits. Rev = `0`.
 *    Hint = `null`.
 *
 * Pure / deterministic; no I/O, no clock reads.
 */
export function planPqrDefaults(input: PqrPlannerInput): PqrPlannerResult {
  const liveReports = input.reports.filter(isLive)

  // Resolve the input train-identity. Multi-weld input wins; single
  // `weldId` is treated as `[weldId]`. Empty/unspecified collapses to
  // the "no priors" branch.
  const inputTrain = trainKey(
    input.weldIds && input.weldIds.length > 0 ? input.weldIds : input.weldId ? [input.weldId] : []
  )

  const forThisTrain = inputTrain
    ? liveReports.filter((r) => trainKey(reportWeldSet(r)) === inputTrain)
    : []

  if (forThisTrain.length === 0) {
    // Prefer the weld id as PQR No. (Kenny/Dan: e.g. 5037-429-IWM-02).
    const singleWeldId =
      input.weldIds && input.weldIds.length === 1
        ? input.weldIds[0]
        : input.weldId
          ? input.weldId
          : ''
    if (singleWeldId.trim()) {
      return {
        defaultPqrNumber: singleWeldId.trim(),
        defaultRevision: '0',
        lastGeneratedHint: null
      }
    }
    const uniquePqrNos = new Set<string>()
    for (const r of liveReports) {
      if (typeof r.pqrNumber === 'string' && r.pqrNumber.length > 0) {
        uniquePqrNos.add(r.pqrNumber)
      }
    }
    const nextIndex = uniquePqrNos.size + 1
    return {
      defaultPqrNumber: `PQR-${String(nextIndex).padStart(3, '0')}`,
      defaultRevision: '0',
      lastGeneratedHint: null
    }
  }

  // Most-recent surviving row for the train (across all PQR Nos used
  // on this train) — drives both the proposed PQR No. and the hint.
  const sortedByTime = [...forThisTrain].sort(
    (a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || '')
  )
  const latest = sortedByTime[0]
  const proposedPqrNumber =
    typeof latest.pqrNumber === 'string' && latest.pqrNumber.length > 0
      ? latest.pqrNumber
      : 'PQR-001'

  // Bump is scoped to the (train, proposedPqrNumber) — moving to a
  // different PQR No. would reset to 0 in the picker, but THIS function
  // only proposes; it doesn't apply the reset.
  let maxRev = -1
  for (const r of forThisTrain) {
    if (r.pqrNumber === proposedPqrNumber) {
      const n = revAsNumber(r.revision)
      if (n > maxRev) maxRev = n
    }
  }
  const defaultRevision = String(Math.max(maxRev + 1, 0))

  const generatedByEmail =
    latest.generatedBy && typeof latest.generatedBy.email === 'string' && latest.generatedBy.email.length > 0
      ? latest.generatedBy.email
      : null

  return {
    defaultPqrNumber: proposedPqrNumber,
    defaultRevision,
    lastGeneratedHint: {
      pqrNumber: proposedPqrNumber,
      revision: typeof latest.revision === 'string' ? latest.revision : '0',
      uploadedAt: latest.uploadedAt || '',
      generatedByEmail
    }
  }
}
